import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Typography, Radio, Space, Button, Alert, message, Spin, Tabs, Switch, Form } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleOutlined, ExperimentOutlined, TagsOutlined, UnorderedListOutlined, HomeOutlined, BankOutlined, ShopOutlined, DatabaseOutlined, AppstoreOutlined, CarOutlined, LineChartOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import Categories from './Categories';
import Variants from './Variants';
import UnitSets from './UnitSets';
import Warehouses from './Warehouses';
import Branches from './Branches';
import Channels from './Channels';

const { Title, Text } = Typography;

const COSTING_OPTIONS = [
  {
    value: 1,
    title: 'Ağırlıklı Ortalama Maliyet',
    description: 'Her yeni alışta toplam maliyet ve miktar güncellenir, satışlarda ortalama maliyet kullanılır. Küçük ve orta ölçekli işletmeler için önerilen, hızlı ve stabil yöntemdir.'
  },
  {
    value: 2,
    title: 'FIFO (İlk Giren İlk Çıkar)',
    description: 'Her alış ayrı katman olarak saklanır, satışlarda en eski katmanlardan tüketilir. Enflasyonist dönemlerde daha gerçekçi kârlılık sağlar fakat daha detaylı veri tutar.'
  }
];

const StockSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [costingMethod, setCostingMethod] = useState<number>(1);
  const [initialValue, setInitialValue] = useState<number>(1);
  const [requirePaymentOnDelivery, setRequirePaymentOnDelivery] = useState(false);
  const [allowPartialPaymentDelivery, setAllowPartialPaymentDelivery] = useState(true);
  const [serviceSettingsInitial, setServiceSettingsInitial] = useState<{ req: boolean; allow: boolean } | null>(null);
  const [purchasePriceStrategy, setPurchasePriceStrategy] = useState<string>('Last');
  const [purchasePriceStrategyInitial, setPurchasePriceStrategyInitial] = useState<string>('Last');
  const activeTab = searchParams.get('tab') || 'categories';

  const hasChanges = useMemo(() => costingMethod !== initialValue, [costingMethod, initialValue]);
  const hasServiceChanges = useMemo(
    () => serviceSettingsInitial != null && (requirePaymentOnDelivery !== serviceSettingsInitial.req || allowPartialPaymentDelivery !== serviceSettingsInitial.allow),
    [requirePaymentOnDelivery, allowPartialPaymentDelivery, serviceSettingsInitial]
  );
  const hasPurchaseStrategyChanges = useMemo(
    () => purchasePriceStrategy !== purchasePriceStrategyInitial,
    [purchasePriceStrategy, purchasePriceStrategyInitial]
  );

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Record<string, string>>('/generalsettings');
      const rawValue = data?.CostingMethod ?? '1';
      const numericValue = parseInt(rawValue, 10);
      const validValue = Number.isNaN(numericValue) ? 1 : numericValue;

      setCostingMethod(validValue);
      setInitialValue(validValue);
      const req = (data?.RequirePaymentOnDelivery ?? 'false').toLowerCase() === 'true';
      const allow = (data?.AllowPartialPaymentDelivery ?? 'true').toLowerCase() === 'true';
      setRequirePaymentOnDelivery(req);
      setAllowPartialPaymentDelivery(allow);
      setServiceSettingsInitial({ req, allow });
      const pps = data?.ProductPurchasePriceStrategy?.trim();
      const strategy = pps === 'Highest' || pps === 'Average' || pps === 'Last' ? pps : 'Last';
      setPurchasePriceStrategy(strategy);
      setPurchasePriceStrategyInitial(strategy);
    } catch (error) {
      console.error('Genel ayarlar yüklenirken hata oluştu:', error);
      message.error('Genel ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/generalsettings', {
        CostingMethod: costingMethod.toString()
      });
      message.success('Ayarlar başarıyla kaydedildi');
      setInitialValue(costingMethod);
    } catch (error: any) {
      console.error('Genel ayarlar kaydedilirken hata oluştu:', error);
      message.error(error?.response?.data?.message || 'Ayarlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePurchasePriceStrategy = async () => {
    setSaving(true);
    try {
      await api.post('/generalsettings', {
        ProductPurchasePriceStrategy: purchasePriceStrategy,
      });
      message.success('Ürün fiyat stratejisi kaydedildi');
      setPurchasePriceStrategyInitial(purchasePriceStrategy);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Strateji kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveServiceSettings = async () => {
    setSaving(true);
    try {
      await api.post('/generalsettings', {
        RequirePaymentOnDelivery: requirePaymentOnDelivery ? 'true' : 'false',
        AllowPartialPaymentDelivery: allowPartialPaymentDelivery ? 'true' : 'false'
      });
      message.success('Servis ayarları kaydedildi');
      setServiceSettingsInitial({ req: requirePaymentOnDelivery, allow: allowPartialPaymentDelivery });
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Servis ayarları kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3}>Stok Ayarları</Title>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setSearchParams({ tab: key });
          }}
          items={[
            {
              key: 'categories',
              label: (
                <span>
                  <TagsOutlined /> Kategoriler
                </span>
              ),
              children: <Categories />,
            },
            {
              key: 'variants',
              label: (
                <span>
                  <UnorderedListOutlined /> Varyantlar
                </span>
              ),
              children: <Variants />,
            },
            {
              key: 'unitsets',
              label: (
                <span>
                  <AppstoreOutlined /> Birim Setleri
                </span>
              ),
              children: <UnitSets />,
            },
            {
              key: 'warehouses',
              label: (
                <span>
                  <HomeOutlined /> Depolar
                </span>
              ),
              children: <Warehouses />,
            },
            {
              key: 'branches',
              label: (
                <span>
                  <BankOutlined /> Şubeler
                </span>
              ),
              children: <Branches />,
            },
            {
              key: 'channels',
              label: (
                <span>
                  <ShopOutlined /> Satış Kanalları
                </span>
              ),
              children: <Channels />,
            },
            {
              key: 'service',
              label: (
                <span>
                  <CarOutlined /> Servis
                </span>
              ),
              children: (
                <Card title="Servis fişi / Tahsilat ayarları" bordered={false}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Form layout="vertical">
                      <Form.Item
                        label="Teslimde tahsilat zorunlu mu?"
                        extra="Araç teslim edilirken tahsilat yapılmadan teslim engellensin mi?"
                      >
                        <Switch
                          checked={requirePaymentOnDelivery}
                          onChange={setRequirePaymentOnDelivery}
                          checkedChildren="Evet"
                          unCheckedChildren="Hayır"
                        />
                      </Form.Item>
                      <Form.Item
                        label="Kısmi tahsilat sonrası araç teslim edilebilir mi?"
                        extra="Borç tamamen kapanmadan araç teslim edilebilsin mi?"
                      >
                        <Switch
                          checked={allowPartialPaymentDelivery}
                          onChange={setAllowPartialPaymentDelivery}
                          checkedChildren="Evet"
                          unCheckedChildren="Hayır"
                        />
                      </Form.Item>
                    </Form>
                    <Space>
                      <Button type="primary" onClick={handleSaveServiceSettings} loading={saving} disabled={!hasServiceChanges}>
                        Kaydet
                      </Button>
                    </Space>
                  </Space>
                </Card>
              ),
            },
            {
              key: 'purchase-price-strategy',
              label: (
                <span>
                  <LineChartOutlined /> Ürün Fiyat Stratejisi
                </span>
              ),
              children: (
                <Card title="Mal alış fişi — kart alış fiyatı (ProductPrice)" bordered={false}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                      type="info"
                      showIcon
                      message="Sadece mal alış fişi kaydında uygulanır"
                      description="Ürün kartına yazılan alış fiyatı ve otomatik kar marjıyla hesaplanan perakende/toptan fiyatlar bu kurala göre belirlenir. Ortalama, veritabanındaki tüm mal alış fişi satır birim fiyatlarının aritmetik ortalamasıdır (miktar ağırlığı yok)."
                    />
                    <Radio.Group
                      value={purchasePriceStrategy}
                      onChange={(e) => setPurchasePriceStrategy(e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Card
                          type={purchasePriceStrategy === 'Last' ? 'inner' : undefined}
                          size="small"
                          hoverable
                          onClick={() => setPurchasePriceStrategy('Last')}
                          style={{
                            borderColor: purchasePriceStrategy === 'Last' ? '#1677ff' : undefined,
                            cursor: 'pointer',
                          }}
                        >
                          <Space align="start">
                            <Radio value="Last" />
                            <Space direction="vertical" size={4}>
                              <Text strong>Son fiyat</Text>
                              <Text type="secondary">Fişteki birim fiyat doğrudan kart alış fiyatı olarak kaydedilir (varsayılan).</Text>
                            </Space>
                          </Space>
                        </Card>
                        <Card
                          type={purchasePriceStrategy === 'Highest' ? 'inner' : undefined}
                          size="small"
                          hoverable
                          onClick={() => setPurchasePriceStrategy('Highest')}
                          style={{
                            borderColor: purchasePriceStrategy === 'Highest' ? '#1677ff' : undefined,
                            cursor: 'pointer',
                          }}
                        >
                          <Space align="start">
                            <Radio value="Highest" />
                            <Space direction="vertical" size={4}>
                              <Text strong>En yüksek fiyat</Text>
                              <Text type="secondary">
                                Yeni birim fiyat, karttaki son alış fiyatından düşükse güncelleme yapılmaz; daha yüksekse yeni fiyat yazılır.
                              </Text>
                            </Space>
                          </Space>
                        </Card>
                        <Card
                          type={purchasePriceStrategy === 'Average' ? 'inner' : undefined}
                          size="small"
                          hoverable
                          onClick={() => setPurchasePriceStrategy('Average')}
                          style={{
                            borderColor: purchasePriceStrategy === 'Average' ? '#1677ff' : undefined,
                            cursor: 'pointer',
                          }}
                        >
                          <Space align="start">
                            <Radio value="Average" />
                            <Space direction="vertical" size={4}>
                              <Text strong>Ortalama fiyat</Text>
                              <Text type="secondary">
                                Bu ürün (ve varsa varyant) için tüm mal alış fişi satırlarının birim fiyatlarının ortalaması kart alış fiyatı olarak kaydedilir.
                              </Text>
                            </Space>
                          </Space>
                        </Card>
                      </Space>
                    </Radio.Group>
                    <Space>
                      <Button
                        type="primary"
                        onClick={handleSavePurchasePriceStrategy}
                        loading={saving}
                        disabled={!hasPurchaseStrategyChanges}
                      >
                        Kaydet
                      </Button>
                    </Space>
                  </Space>
                </Card>
              ),
            },
            {
              key: 'costing',
              label: (
                <span>
                  <DatabaseOutlined /> Stok Maliyet Yönetimi
                </span>
              ),
              children: (
                <Card title="Stok Maliyet Yöntemi" bordered={false}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                      type="info"
                      showIcon
                      message="Yeni işlemler için geçerli"
                      description="Yöntemi değiştirdikten sonra sadece yeni stok hareketleri seçilen metoda göre hesaplanır. Mevcut stok ve geçmiş fişler korunur."
                    />

                    <Radio.Group
                      value={costingMethod}
                      onChange={(event) => setCostingMethod(event.target.value)}
                      style={{ width: '100%' }}
                    >
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {COSTING_OPTIONS.map((option) => (
                          <Card
                            key={option.value}
                            type={costingMethod === option.value ? 'inner' : undefined}
                            hoverable
                            onClick={() => setCostingMethod(option.value)}
                            style={{
                              borderColor: costingMethod === option.value ? '#1677ff' : undefined,
                              cursor: 'pointer'
                            }}
                            bodyStyle={{ padding: 20 }}
                          >
                            <Space align="start" size="large" style={{ width: '100%' }}>
                              <Radio value={option.value} />
                              <Space direction="vertical" size={4} style={{ flex: 1 }}>
                                <Space align="center" size="small">
                                  {option.value === 1 ? (
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                  ) : (
                                    <ExperimentOutlined style={{ color: '#faad14' }} />
                                  )}
                                  <Text strong>{option.title}</Text>
                                </Space>
                                <Text type="secondary">{option.description}</Text>
                              </Space>
                            </Space>
                          </Card>
                        ))}
                      </Space>
                    </Radio.Group>

                    <Alert
                      type="warning"
                      showIcon
                      message="FIFO seçeneği daha fazla veri saklar"
                      description="FIFO aktif olduğunda her alış işlemi için katman kaydı oluşturulur. Veri boyutu artabilir ancak kârlılık hesapları daha hassas olur."
                    />

                    <Space>
                      <Button type="primary" onClick={handleSave} loading={saving} disabled={!hasChanges}>
                        Ayarları Kaydet
                      </Button>
                      <Button onClick={loadSettings} disabled={saving}>
                        Yenile
                      </Button>
                    </Space>
                  </Space>
                </Card>
              ),
            },
          ]}
        />
      </Space>
    </Spin>
  );
};

export default StockSettings;

